import moment from "moment-timezone";

export const HELP_TEXT = "Term contract = Eezee's tender price for this enterprise. A vipPrice is a seller's discount. "
    + "When both exist, the term contract price wins.";

export const BACKING_SUPPLIER_LABEL = "Backing supplier (who we buy from)";

/* The lines xlsx, in the server's column order. Download writes all of them; upload reads only LINES_XLSX_EDITABLE. */
export const LINES_XLSX_MANDATORY = ["productId", "price"];
export const LINES_XLSX_OPTIONAL = ["costPrice", "backingSellerId", "maxQuantity"]; // backingSellerId = IST seller org id; maxQuantity blank = no cap
export const LINES_XLSX_EDITABLE = [...LINES_XLSX_MANDATORY, ...LINES_XLSX_OPTIONAL];
export const LINES_XLSX_COLUMNS = [...LINES_XLSX_EDITABLE, "productTitle", "sku", "supplierId", "supplierCompanyName", "backingSellerCompanyName"];

export const TIMEZONE = "Asia/Singapore";

export const STATUS_COLORS = { Active: "green", Upcoming: "blue", Ended: "", Deleted: "red" };

/* the list's status segmented control, in the server's vocabulary */
export const LIST_STATUSES = ["active", "upcoming", "ended", "deleted", "all"];

export const ENDING_SOON_DAYS = 30;

/* The four live line flags (eezee-server term-contract-flags FLAG_NAMES): chip label, and the text under a flagged line. */
export const FLAGS = [
    { name: "noBackingSeller", chip: "No backing supplier", text: "No backing supplier" },
    { name: "notInPrivateCatalog", chip: "Not in private catalog", text: "Not in this enterprise's private catalog" },
    { name: "hasActiveVipPrice", chip: "Also has VIP price", text: "Also has an active VIP price (contract price wins)" },
    { name: "negativeMargin", chip: "Negative margin", text: "Contract price is below cost" }
];

/* The server stores a YYYY-MM-DD as a whole Singapore day, so dates are read and sent in SGT. */
export const toDay = ({
    date,
}) => date ? moment.tz(date, TIMEZONE).format("YYYY-MM-DD") : "";

export const toDateTime = ({
    date,
}) => date ? moment.tz(date, TIMEZONE).format("YYYY-MM-DD HH:mm") : "";

export const contractStatus = ({
    contract,
    now = new Date(),
}) => {
    if (contract.deletedAt) return "Deleted";
    if (new Date(contract.startDate) > now) return "Upcoming";
    if (new Date(contract.endDate) < now) return "Ended";
    return "Active";
};

/** Whole Singapore days from today to `date`: 0 = today, negative = past. Same rule as the server's endsInDays. */
export const daysUntil = ({
    date,
    now = new Date(),
}) => moment.tz(date, TIMEZONE).startOf("day").diff(moment.tz(now, TIMEZONE).startOf("day"), "days");

/** The status badge: "Ends in N days" (amber) inside the last 30 days of an active contract. */
export const contractBadge = ({
    contract,
    now = new Date(),
}) => {
    const status = contractStatus({ contract, now });
    if (status === "Upcoming") {
        return { text: `Starts ${moment.tz(contract.startDate, TIMEZONE).format("DD MMM YYYY")}`, color: STATUS_COLORS.Upcoming };
    }
    if (status !== "Active") {
        return { text: status, color: STATUS_COLORS[status] };
    }
    const days = daysUntil({ date: contract.endDate, now });
    if (days > ENDING_SOON_DAYS) {
        return { text: status, color: STATUS_COLORS.Active };
    }
    return { text: days === 0 ? "Ends today" : `Ends in ${days} day${days === 1 ? "" : "s"}`, color: "orange" };
};

export const formatPeriod = ({
    contract,
}) => `${moment.tz(contract.startDate, TIMEZONE).format("DD MMM YYYY")} – ${moment.tz(contract.endDate, TIMEZONE).format("DD MMM YYYY")}`;

const isNumber = ({
    value,
}) => typeof value === "number" && !isNaN(value);

/** "−4.8% vs list", or "" when either price is missing or the difference rounds to 0.0%. */
export const pctVsList = ({
    price,
    listPrice,
}) => {
    if (!isNumber({ value: price }) || !isNumber({ value: listPrice }) || listPrice === 0) return "";
    const pct = (price - listPrice) / listPrice * 100;
    const rounded = Math.abs(pct).toFixed(1);
    if (rounded === "0.0") return "";
    return `${pct < 0 ? "−" : "+"}${rounded}% vs list`;
};

/** (price − cost) / price as a %, or null without both. Same formula as the server's avgMarginPct. */
export const marginPct = ({
    price,
    costPrice,
}) => isNumber({ value: price }) && isNumber({ value: costPrice }) && price !== 0 ? (price - costPrice) / price * 100 : null;

export const formatPct = ({
    value,
}) => value === null || value === undefined ? "—" : `${value.toFixed(1)}%`;

/** A bulk action's target: every line matching the filter ("select all N"), else the ticked ids. */
export const bulkTarget = ({
    lineIds,
    allMatching,
    filter,
}) => allMatching
    ? { filter: { q: filter.q || undefined, flag: filter.flag || undefined, listingSupplierId: filter.listingSupplierId || undefined } }
    : { lineIds };

/** The server's `{ success: false, message }`, else the axios error. */
export const errorMessage = ({
    error,
}) => (error && error.response && error.response.data && error.response.data.message) || (error && error.message) || "Request failed";

/** One line per non-empty warning group; [] when there is nothing to show. */
export const warningLines = ({
    warnings,
}) => {
    const { notInPrivateCatalog = [], hasActiveVipPrice = [], errors = [] } = warnings || {};
    return [
        notInPrivateCatalog.length && `Not in this enterprise's private catalog: ${notInPrivateCatalog.join(", ")}`,
        hasActiveVipPrice.length && `Also has an active vipPrice for this enterprise (the term contract price wins): ${hasActiveVipPrice.join(", ")}`,
        ...errors.map(error => `Check could not run: ${error}`)
    ].filter(Boolean);
};

/** { created: 3, error: 1, ... } from the xlsx upload's per-row results. */
export const countByStatus = ({
    results,
}) => (results || []).reduce(
    (counts, row) => ({ ...counts, [row.status]: (counts[row.status] || 0) + 1 }),
    {}
);

/* The enterprise search endpoint builds a regex from the term. */
export const escapeRegex = ({
    text,
}) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** "" → null so the server clears the field; otherwise a number (NaN when not numeric). */
export const toNumberOrNull = ({
    value,
}) => value === "" || value === null || value === undefined ? null : Number(value);

/** Hands a downloaded blob to the browser as a file. */
export const saveBlob = ({
    blob,
    fileName,
}) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
};



// WEBPACK FOOTER //
// ./src/components/term-contract/helpers.js